import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { parseRangeValue } from 'assets/utils/parsersAndValidation/Parsers';
import { HttpStatus } from 'config/Api.Config';
import { compact, isEmpty, uniq } from 'lodash';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import claimTypeDealerAutomationRuleDeleteApi from 'models/dealerManagement/claimTypeDealerAutomationRule/delete/Api.ClaimTypeDealerAutomationRule.Delete';
import ConditionTypes from 'models/enums/ConditionTypes';
import EffectTypes from 'models/enums/EffectTypes';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { DealerForm, useDealerProvider } from '../../Dealer.Hooks';

export default function DealerClaimTypeAutomationRules({
  additionalClaimTypeIds,
}: {
  additionalClaimTypeIds: number[];
}) {
  const { lang } = getLocales();
  const { form } = useModalForm<DealerForm>();
  const { selected } = useDealerProvider();
  const { claimStatuses, claimJobStatuses } = useClaimStatuses();

  const claimTypeIds = useMemo(() => {
    return uniq([...compact(selected?.dealerClaimTypes?.map((c) => c.claimTypeId)), ...additionalClaimTypeIds]);
  }, [selected?.dealerClaimTypes, additionalClaimTypeIds]);

  const { payload: claimTypesPayload } = useApi(
    {
      action: ClaimType.list,
      wait: !claimTypeIds.length,
      body: { id: claimTypeIds },
    },
    [claimTypeIds]
  );

  const updateRule = (id: number, newData: any, claimTypeId: number) => {
    const updated = form.data?.claimTypeDealerAutomationRules?.map((entry) =>
      entry.claimTypeAutomationRuleId === id
        ? {
            ...entry,
            ...newData,
            claimTypeAutomationRule: {
              claimTypeId: claimTypeId,
            },
          }
        : entry
    );
    form.update({ claimTypeDealerAutomationRules: updated });
  };
  const deleteRule = async (claimTypeRuleId: number, ruleId?: number, claimTypeId?: number) => {
    const updatedRules = form.data?.claimTypeDealerAutomationRules?.filter(
      (it) => it.claimTypeAutomationRuleId !== claimTypeRuleId
    );
    form.update({ claimTypeDealerAutomationRules: updatedRules });

    setOverrideClaimRule((prev) => ({
      ...prev,
      [claimTypeId]: false,
    }));

    if (ruleId) {
      const res = await claimTypeDealerAutomationRuleDeleteApi({ id: ruleId });
      if (res.status !== HttpStatus.ok) {
        addLog({ error: res.message });
      }
    }
  };
  const updateEnabled = (claimTypeId: number, newData: { isClaimTypeAutomationEnabled: any }) => {
    const currentDealerClaimTypes = [...(form.data.dealerClaimTypes || [])];
    const updated = currentDealerClaimTypes.map((entry) =>
      entry.claimTypeId === claimTypeId ? { ...entry, ...newData } : entry
    );
    form.update({ dealerClaimTypes: updated });
  };
  const [overrideClaimRule, setOverrideClaimRule] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (isEmpty(overrideClaimRule)) {
      const initialOverrides: Record<string, boolean> = {};

      claimTypesPayload?.data?.forEach((claimType) => {
        const automationRuleIds = claimType.claimTypeAutomationRules?.map((r) => r.id) ?? [];

        const hasDealerOverride = (form.data?.claimTypeDealerAutomationRules ?? []).some((dealerRule) =>
          automationRuleIds.includes(dealerRule.claimTypeAutomationRuleId)
        );

        if (hasDealerOverride) initialOverrides[claimType.id] = hasDealerOverride;
      });

      setOverrideClaimRule(initialOverrides);
    }
  }, [claimTypesPayload?.data, form.data?.claimTypeDealerAutomationRules]);

  return (
    <FormWrapper>
      <DataMap
        data={claimTypesPayload?.data}
        render={({ data: claimType }) => {
          const dealerClaimType = form.data?.dealerClaimTypes?.find((it) => it.claimTypeId === claimType.id);

          return (
            !!claimType.claimTypeAutomationRules.length &&
            dealerClaimType && (
              <Fragment key={claimType.id}>
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: Theme.Size.M,
                    }}
                  >
                    <h3>{claimType.name}</h3>
                    <CheckWrapper>
                      <CheckInput
                        name="isClaimTypeAutomationEnabled"
                        label={lang.enabled}
                        value={dealerClaimType?.isClaimTypeAutomationEnabled}
                        onChange={({ isClaimTypeAutomationEnabled }) =>
                          updateEnabled(claimType.id, { isClaimTypeAutomationEnabled })
                        }
                        class={inputStyles.check.switch}
                        htmlElementProps={{
                          title: dealerClaimType?.isClaimTypeAutomationEnabled ? lang.disable : lang.enable,
                        }}
                      />
                    </CheckWrapper>
                  </div>
                </div>
                &nbsp;
                {dealerClaimType?.isClaimTypeAutomationEnabled && (
                  <>
                    <DataMap
                      data={claimType.claimTypeAutomationRules}
                      render={({ data: rule }) => {
                        const currentRule = form.data?.claimTypeDealerAutomationRules?.find(
                          (c) => c.claimTypeAutomationRuleId === rule.id
                        );

                        const condition = currentRule?.conditions?.[0];
                        const isRange = condition?.type === ConditionTypes.Range;
                        const rangeValue = isRange ? parseRangeValue(condition?.value) : { min: 0, max: 0 };

                        return (
                          <div key={rule.id}>
                            <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: '10px',
                                }}
                              >
                                <h3>{lang.claimRule}</h3>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: Theme.Size.M,
                                }}
                              >
                                <span
                                  style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}
                                >
                                  If Claim TotalAmountCAD is:
                                  <strong>{rule.conditions[0]?.type}</strong>
                                  {rule.conditions[0]?.type !== ConditionTypes.Range ? (
                                    <>
                                      to:
                                      <strong>{rule.conditions[0]?.value}</strong>
                                    </>
                                  ) : (
                                    <>
                                      between <strong>{parseRangeValue(rule.conditions[0]?.value)?.min}</strong> and
                                      <strong>{parseRangeValue(rule.conditions[0]?.value)?.max}</strong>
                                    </>
                                  )}
                                  set ClaimStatus to:
                                  <strong>{rule?.effects?.[0]?.value}</strong>
                                  and ClaimJobStatus to:
                                  <strong>{rule?.effects?.[1]?.value}</strong>
                                </span>
                                <div style={{ alignSelf: 'flex-start' }}>
                                  <CheckWrapper>
                                    <CheckInput
                                      name="overrideClaimRule"
                                      label={lang.overrideClaimRule}
                                      value={overrideClaimRule[claimType.id]}
                                      onChange={({ overrideClaimRule: isOverrided }) => {
                                        setOverrideClaimRule((prev) => ({ ...prev, [claimType.id]: isOverrided }));
                                        const currentRules = form.data?.claimTypeDealerAutomationRules || [];
                                        if (isOverrided) {
                                          const alreadyExists = currentRules.some(
                                            (it) => it.claimTypeAutomationRuleId === rule.id
                                          );
                                          if (alreadyExists) return;

                                          const newEntry = {
                                            claimTypeId: claimType.id,
                                            claimTypeAutomationRuleId: rule.id,
                                            claimTypeAutomationRule: {
                                              claimTypeId: claimType.id,
                                            },
                                            conditions: [
                                              {
                                                type: ConditionTypes.Equal,
                                                propertyPath: 'Claim.TotalAmountCAD',
                                                value: '',
                                              },
                                            ],
                                            effects: [
                                              { type: EffectTypes.Assign, propertyPath: 'Claim.StatusCode', value: '' },
                                              {
                                                type: EffectTypes.Assign,
                                                propertyPath: 'Claim.ClaimJobs[].StatusCode',
                                                value: '',
                                              },
                                            ],
                                          } as Model.IClaimTypeDealerAutomationRule;

                                          form.update({ claimTypeDealerAutomationRules: [...currentRules, newEntry] });
                                        } else {
                                          const filtered = currentRules.filter(
                                            (r) => !(r.claimTypeAutomationRuleId === rule.id)
                                          );
                                          form.update({ claimTypeDealerAutomationRules: filtered });
                                        }
                                      }}
                                      disabled={(form.data?.claimTypeDealerAutomationRules ?? []).some(
                                        (it) => it.claimTypeAutomationRuleId === rule.id && it.id
                                      )}
                                      class={inputStyles.check.radio}
                                    />
                                  </CheckWrapper>
                                </div>
                              </div>
                            </Box>
                            {overrideClaimRule[claimType.id] && (
                              <div>
                                <h3>{lang.overrideClaimRule}</h3>
                                <span
                                  key={rule.id}
                                  style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}
                                >
                                  If Claim TotalAmountCAD is:
                                  <SelectInput
                                    name="type"
                                    value={condition?.type}
                                    data={Object.values(ConditionTypes).map((it) => ({ id: it, title: it }))}
                                    onChange={({ type }) => {
                                      const baseValue =
                                        type === ConditionTypes.Range ? JSON.stringify({ min: 0, max: 0 }) : '';
                                      updateRule(
                                        currentRule?.claimTypeAutomationRuleId,
                                        {
                                          conditions: [
                                            { type, propertyPath: 'Claim.TotalAmountCAD', value: baseValue },
                                          ],
                                        },
                                        claimType.id
                                      );
                                    }}
                                    style={{ width: '100px', input: { textAlign: 'center' } }}
                                  />
                                  {!isRange ? (
                                    <>
                                      to:
                                      <NumberInput
                                        name="value"
                                        step={0.01}
                                        value={Number(condition?.value)}
                                        onChange={({ value }) =>
                                          updateRule(
                                            currentRule?.claimTypeAutomationRuleId,
                                            {
                                              conditions: [{ ...condition, value: value?.toString() }],
                                            },
                                            claimType.id
                                          )
                                        }
                                        style={{ width: '150px', input: { textAlign: 'center' } }}
                                        iconBefore={<Icon class="fas-dollar-sign" />}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      between
                                      <NumberInput
                                        name="minValue"
                                        step={0.01}
                                        value={rangeValue?.min ?? 0}
                                        onChange={({ minValue }) =>
                                          updateRule(
                                            currentRule?.claimTypeAutomationRuleId,
                                            {
                                              conditions: [
                                                {
                                                  ...condition,
                                                  value: JSON.stringify({
                                                    min: minValue,
                                                    max: rangeValue?.max ?? 0,
                                                  }),
                                                },
                                              ],
                                            },
                                            claimType.id
                                          )
                                        }
                                        style={{ width: '150px', input: { textAlign: 'center' } }}
                                        iconBefore={<Icon class="fas-dollar-sign" />}
                                      />
                                      and
                                      <NumberInput
                                        name="maxValue"
                                        step={0.01}
                                        value={rangeValue?.max ?? 0}
                                        onChange={({ maxValue }) =>
                                          updateRule(
                                            currentRule?.claimTypeAutomationRuleId,
                                            {
                                              conditions: [
                                                {
                                                  ...condition,
                                                  value: JSON.stringify({
                                                    min: rangeValue?.min ?? 0,
                                                    max: maxValue,
                                                  }),
                                                },
                                              ],
                                            },
                                            claimType.id
                                          )
                                        }
                                        style={{ width: '150px', input: { textAlign: 'center' } }}
                                        iconBefore={<Icon class="fas-dollar-sign" />}
                                      />
                                    </>
                                  )}
                                  set ClaimStatus to:
                                  <SelectInput
                                    name="claimStatus"
                                    data={claimStatuses}
                                    value={currentRule?.effects?.[0]?.value}
                                    onChange={({ claimStatus }) =>
                                      updateRule(
                                        currentRule?.claimTypeAutomationRuleId,
                                        {
                                          effects: [
                                            { ...currentRule?.effects?.[0], value: claimStatus },
                                            currentRule?.effects?.[1],
                                          ],
                                        },
                                        claimType.id
                                      )
                                    }
                                    style={{
                                      width: '225px',
                                      input: { textAlign: 'center', fontSize: Theme.FontSize.S },
                                    }}
                                  />
                                  and ClaimJobStatus to:
                                  <SelectInput
                                    name="claimJobStatus"
                                    data={claimJobStatuses}
                                    value={currentRule?.effects?.[1]?.value}
                                    onChange={({ claimJobStatus }) =>
                                      updateRule(
                                        currentRule?.claimTypeAutomationRuleId,
                                        {
                                          effects: [
                                            currentRule?.effects?.[0],
                                            { ...currentRule?.effects?.[1], value: claimJobStatus },
                                          ],
                                        },
                                        claimType.id
                                      )
                                    }
                                    style={{
                                      width: '225px',
                                      input: { textAlign: 'center', fontSize: Theme.FontSize.S },
                                    }}
                                  />
                                  <Button
                                    class={ThemeButtonCircle}
                                    media={<Icon class="fas-trash" />}
                                    onClick={() => deleteRule(rule.id, currentRule?.id, claimType?.id)}
                                  />
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                  </>
                )}
                <br />
              </Fragment>
            )
          );
        }}
      />
    </FormWrapper>
  );
}
